import { AxiosResponse } from 'axios';
import { axiosRequest } from '@/common/api/instance';
import { Meta, Response as ApiResponse } from '@/types/api/response.types';
import {
  BOQ,
  BoqBody,
  ContractType,
  CreateSiteBody,
  Draw,
  Site,
  SiteUser,
  SiteUserResponse,
  SiteUserRole,
  UpdateCashflow,
  UpdateSiteBody,
} from '@/types/api/sites.types';
import { Cashflow, CashflowGraph, CashflowMonth, CashflowParams } from '@/types/api/project.types';

const resource = '/sites';

export const createSite = async (
  data: CreateSiteBody,
): Promise<AxiosResponse<ApiResponse<Site>>> => {
  return axiosRequest().post(`${resource}/`, data);
};

export const getSite = async (id: string): Promise<AxiosResponse<ApiResponse<Site>>> => {
  return axiosRequest().get(`${resource}/${id}`);
};

export const getSiteUsers = async (
  id: string,
  data: { roles: string[]; includesPending?: boolean },
): Promise<AxiosResponse<ApiResponse<SiteUserResponse[]>>> => {
  // Convert the roles array to the required query string format
  const params = new URLSearchParams();
  if (data.roles && Array.isArray(data.roles)) {
    data.roles.forEach((role) => params.append('roles', role));
  }

  if (data.includesPending !== undefined) {
    params.append('includesPending', data.includesPending.toString());
  }

  const url = `${resource}/${id}/users?${params.toString()}`;

  return axiosRequest().get(url);
};

export const updateSite = async (
  id: string,
  data: UpdateSiteBody,
): Promise<AxiosResponse<ApiResponse<Site>>> => {
  return axiosRequest().put(`${resource}/${id}`, data);
};

export const addUser = async (
  id: string,
  data: SiteUser[],
): Promise<AxiosResponse<ApiResponse<SiteUserRole[]>>> => {
  return axiosRequest().post(`${resource}/${id}/users`, data);
};

export const removeUser = async (id: string): Promise<AxiosResponse<ApiResponse<Meta>>> => {
  return axiosRequest().delete(`${resource}/users/${id}`);
};

export const createBoq = async (
  siteId: string,
  data?: BoqBody,
): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  return axiosRequest().post(`${resource}/${siteId}/boqs`, data);
};

export const getBOQ = async (id: string): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  return axiosRequest().get(`${resource}/${id}/boqs`);
};

export const getSellingBOQ = async (id: string): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  return axiosRequest().get(`${resource}/${id}/boqs/selling`);
};

export const getDraws = async (id: string): Promise<AxiosResponse<ApiResponse<Draw[]>>> => {
  return axiosRequest().get(`${resource}/${id}/draws`);
};

export const updateDraw = async (
  id: string,
  data: Partial<Draw>[],
): Promise<AxiosResponse<ApiResponse<Draw>>> => {
  return axiosRequest().patch(`${resource}/${id}/draws`, data);
};

export const getSiteCashflow = async (
  id: string,
  params: CashflowParams,
): Promise<AxiosResponse<ApiResponse<Cashflow>>> => {
  return axiosRequest().get(`${resource}/${id}/cashflows`, { params });
};

export const updateCashflowOverheads = async (
  id: string,
  data: UpdateCashflow,
): Promise<AxiosResponse<ApiResponse<CashflowMonth>>> => {
  return axiosRequest().patch(`${resource}/${id}/cashflow`, data);
};

export const getSiteCashflowGraph = async (
  id: string,
  params: CashflowParams,
): Promise<AxiosResponse<ApiResponse<CashflowGraph>>> => {
  return axiosRequest().get(`${resource}/${id}/cashflows/cumulative-net`, { params });
};

export const updateSiteClaimBannerDisplay = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<Site>>> => {
  return axiosRequest().patch(`${resource}/${id}/cashflows/banner`);
};

export const exportSiteCashflow = (id: string): Promise<AxiosResponse<Blob>> => {
  return axiosRequest().get(`${resource}/${id}/cashflows/export`, { responseType: 'blob' });
};

export const downloadClaimSummary = (id: string): Promise<AxiosResponse<Blob>> => {
  return axiosRequest().get(`${resource}/${id}/draws/summary`, { responseType: 'blob' });
};

export const getContractTypes = (): Promise<AxiosResponse<ApiResponse<ContractType[]>>> => {
  return axiosRequest().get(`${resource}/contract-types`);
};
